import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/api';
import type { Folder } from '../types';

export function useFolders() {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFolders = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiClient.getFolders();
      setFolders(data);
    } catch (err: any) {
      setError(err.error?.message || 'Failed to load folders');
      console.error('Error loading folders:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createFolder = useCallback(async (name: string): Promise<Folder | null> => {
    setError(null);
    try {
      const folder = await apiClient.createFolder(name);
      setFolders(prev => [...prev, folder].sort((a, b) => a.name.localeCompare(b.name)));
      return folder;
    } catch (err: any) {
      setError(err.error?.message || 'Failed to create folder');
      console.error('Error creating folder:', err);
      return null;
    }
  }, []);

  const updateFolder = useCallback(async (id: string, name: string): Promise<boolean> => {
    setError(null);
    try {
      await apiClient.updateFolder(id, name);
      setFolders(prev =>
        prev.map(f => (f.id === id ? { ...f, name, updatedAt: new Date().toISOString() } : f))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      return true;
    } catch (err: any) {
      setError(err.error?.message || 'Failed to update folder');
      console.error('Error updating folder:', err);
      return false;
    }
  }, []);

  const deleteFolder = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    try {
      await apiClient.deleteFolder(id);
      setFolders(prev => prev.filter(f => f.id !== id));
      return true;
    } catch (err: any) {
      setError(err.error?.message || 'Failed to delete folder');
      console.error('Error deleting folder:', err);
      return false;
    }
  }, []);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  return {
    folders,
    isLoading,
    error,
    createFolder,
    updateFolder,
    deleteFolder,
    refreshFolders: loadFolders,
  };
}
